import React, { useState, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { EMPTY_STRING, FormBuilder, RenderConfig, Row, AppContext } from 'commons-ui';
import { DatePicker } from 'commons-ui/src/components/baseui/input';
import { CsvUploadPreview, ParseCsvResult } from 'commons-ui/src/components/upload/index';
import dayjs from 'dayjs';
import { ERROR_MESSAGES, StartSuspiciousTransactionsRequest } from './types';
import { BULK_QUERY_LABELS } from '../../../constants';
import useSuspiciousService from './useSuspiciousService';

const CSV_ROW_LIMIT = 1000;

const parseCsv = (file: File): Promise<ParseCsvResult<string>> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();

		reader.onload = () => {
			try {
				const text: string = (reader.result as string) || EMPTY_STRING;
				const lines: string[] = text.split('\n').filter((l) => l.trim());

				if (lines.length === 0) throw new Error(ERROR_MESSAGES.CSV_EMPTY);

				const header: string = lines[0].trim();
				if (header.replace(/\s/g, '').toLowerCase() !== 'txnid') {
					throw new Error(ERROR_MESSAGES.CSV_INVALID_HEADER);
				}

				const rows: string[] = lines
					.slice(1)
					.map((l) => l.trim())
					.filter(Boolean);

				if (rows.length === 0) throw new Error(ERROR_MESSAGES.CSV_NO_ROWS);
				if (rows.length > CSV_ROW_LIMIT) throw new Error(ERROR_MESSAGES.CSV_TOO_MANY_ROWS(CSV_ROW_LIMIT));

				resolve({ validRows: rows, invalidRows: [] });
			} catch (err) {
				reject(err instanceof Error ? err : new Error(String(err)));
			}
		};

		reader.onerror = () => reject(new Error('Failed to read file'));

		reader.readAsText(file);
	});

const HookFormDatePicker: React.FC<any> = (props) => {
	const { name, watch, setValue, maxDate, minDate } = props;
	const value: Date | undefined = watch(name);
	return (
		<DatePicker
			{...props}
			value={value || null}
			onChange={({ date }: { date: Date | Date[] }) => {
				let newDate: Date | undefined;
				if (Array.isArray(date)) {
					newDate = date.length > 0 ? (date[0] as Date) : undefined;
				} else {
					newDate = date as Date;
				}
				setValue(name, newDate);
			}}
			maxDate={maxDate}
			minDate={minDate}
		/>
	);
};

const CreateSuspiciousTransactions: React.FC = () => {
	const history = useHistory();
	const service = useSuspiciousService();
	const { showAlert } = useContext(AppContext);
	const [fileRows, setFileRows] = useState<string[]>([]);

	const START_DATE_KEY = 'startDate';
	const CSV_KEY = 'csvFile';

	const csvField = new RenderConfig()
		.setName(CSV_KEY)
		.setLabel('CSV File')
		.setRenderer(CsvUploadPreview)
		.setRenderProps({
			isEditable: { create: true },
			required: true,
			accept: '.csv',
			parseCsv,
			columns: ['Idx', 'Txn Id'],
			rowFormatter: (row: string, idx: number) => [idx + 1, row],
			onValidRows: (rows: string[]) => setFileRows(rows)
		});

	const dateField = new RenderConfig()
		.setName(START_DATE_KEY)
		.setLabel('Start Date')
		.setRenderer(HookFormDatePicker)
		.setRenderProps({
			isEditable: { create: true },
			required: true,
			maxDate: new Date()
		});

	const fieldConfig = { [CSV_KEY]: csvField, [START_DATE_KEY]: dateField };
	const layoutConfig = [new Row(START_DATE_KEY), new Row(CSV_KEY)];

	const [showLoader, setShowLoader] = useState(false);

	return (
		<FormBuilder
			data={{ [START_DATE_KEY]: new Date('2024-01-01'), [CSV_KEY]: null }}
			mode='create'
			fieldConfig={fieldConfig}
			layoutConfig={layoutConfig}
			showLoader={showLoader}
			setShowLoader={setShowLoader}
			handleFormSubmit={async (formData: any) => {
				if (!fileRows.length) {
					showAlert(ERROR_MESSAGES.CSV_FILE_REQUIRED, { type: 'error' });
					return;
				}
				const startDateStr: string = dayjs(formData.startDate).format('YYYY-MM-DD');
				setShowLoader(true);
				const payload: StartSuspiciousTransactionsRequest = {
					queryLabel: BULK_QUERY_LABELS.GET_SUSPICIOUS_TRANSACTIONS,
					startDate: startDateStr,
					rows: fileRows
				};
				await service.createRequest(payload);
				setShowLoader(false);
				const basePath: string = history.location.pathname.replace(/\/create$/, '/list');
				history.push(basePath);
			}}
			handleFormCancel={() => window.history.back()}
		/>
	);
};

export default CreateSuspiciousTransactions;
